// PermissionPicker — folded-by-domain permission picker.
// Spec: docs/specs/custom-rbac.md §6.5.
//
// Behaviours:
//   - 18 domains collapsed by default (expand by click)
//   - Per-domain quick actions: 全選 / 全清 / read / read+write
//   - Search box filters labels + ids
//   - Dangerous permissions render with red warning icon
//   - Effective preview footer: "已選 N / 117 個權限"
//
// Uncontrolled-ish: parent holds Set<string>, picker fires onChange(next).

import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, AlertTriangle, Search, X } from "lucide-react";
import {
  PERMISSION_CATALOG,
  PERMISSIONS_BY_DOMAIN,
  DOMAIN_LABELS,
  DANGEROUS_PERMISSIONS,
  type PermissionMeta,
} from "../lib/permissionCatalog.js";

interface Props {
  /** Currently selected permission ids. */
  value: ReadonlySet<string>;
  onChange: (next: Set<string>) => void;
  /** When provided, ids NOT in this set render disabled (cannot grant). */
  actorPermissions?: ReadonlySet<string>;
  /** Read-only mode — render checkboxes disabled. */
  disabled?: boolean;
}

const DOMAIN_ORDER: string[] = Array.from(PERMISSIONS_BY_DOMAIN.keys());

export default function PermissionPicker({ value, onChange, actorPermissions, disabled }: Props): React.ReactElement {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");

  const filtered: PermissionMeta[] = useMemo(() => {
    if (!search.trim()) return [...PERMISSION_CATALOG];
    const q = search.trim().toLowerCase();
    return PERMISSION_CATALOG.filter(
      (p) =>
        p.id.toLowerCase().includes(q) ||
        p.label_zh.toLowerCase().includes(q) ||
        (DOMAIN_LABELS[p.domain] ?? "").toLowerCase().includes(q),
    );
  }, [search]);

  const filteredByDomain: Map<string, PermissionMeta[]> = useMemo(() => {
    const m = new Map<string, PermissionMeta[]>();
    for (const p of filtered) {
      if (!m.has(p.domain)) m.set(p.domain, []);
      m.get(p.domain)!.push(p);
    }
    return m;
  }, [filtered]);

  // When searching, auto-expand matched domains.
  const effectiveExpanded: ReadonlySet<string> = search.trim()
    ? new Set(filteredByDomain.keys())
    : expanded;

  function toggleDomain(d: string): void {
    if (search.trim()) return;
    const next = new Set(expanded);
    if (next.has(d)) next.delete(d);
    else next.add(d);
    setExpanded(next);
  }

  function togglePerm(id: string): void {
    if (disabled) return;
    if (actorPermissions && !actorPermissions.has(id) && !value.has(id)) return;
    const next = new Set(value);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  }

  function applyDomain(d: string, mode: "all" | "none" | "read" | "read_write"): void {
    if (disabled) return;
    const perms = PERMISSIONS_BY_DOMAIN.get(d) ?? [];
    const next = new Set(value);
    for (const p of perms) {
      const allowedByActor = !actorPermissions || actorPermissions.has(p.id);
      switch (mode) {
        case "all":
          if (allowedByActor) next.add(p.id);
          break;
        case "none":
          next.delete(p.id);
          break;
        case "read":
          if (p.category === "read" && allowedByActor) next.add(p.id);
          else next.delete(p.id);
          break;
        case "read_write":
          if ((p.category === "read" || p.category === "write") && allowedByActor) next.add(p.id);
          else next.delete(p.id);
          break;
      }
    }
    onChange(next);
  }

  const totalSelected = value.size;
  const totalAvailable = PERMISSION_CATALOG.length;

  return (
    <div className="space-y-3">
      {/* Search bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="搜尋權限名稱或 id（例：site、users:write、刪除）"
          className="input pl-9 pr-9"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-bg-hover"
            aria-label="清除搜尋"
          >
            <X className="h-3.5 w-3.5 text-text-muted" />
          </button>
        )}
      </div>

      {/* Domain list */}
      <div className="rounded-xl border border-border bg-bg-surface divide-y divide-border">
        {DOMAIN_ORDER.map((d) => {
          const perms = filteredByDomain.get(d) ?? [];
          if (search.trim() && perms.length === 0) return null;
          const allPerms = PERMISSIONS_BY_DOMAIN.get(d) ?? [];
          const selectedHere = allPerms.filter((p) => value.has(p.id)).length;
          const open = effectiveExpanded.has(d);
          return (
            <div key={d}>
              <button
                type="button"
                onClick={() => toggleDomain(d)}
                disabled={!!search.trim()}
                className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-bg-hover transition-colors disabled:cursor-default"
              >
                <div className="flex items-center gap-2 min-w-0">
                  {open ? (
                    <ChevronDown className="h-4 w-4 text-text-muted flex-shrink-0" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-text-muted flex-shrink-0" />
                  )}
                  <span className="text-sm font-medium text-text-primary truncate">
                    {DOMAIN_LABELS[d] ?? d}
                  </span>
                  <span className="text-xs text-text-muted font-mono">({d})</span>
                </div>
                <span
                  className={[
                    "text-xs font-mono px-2 py-0.5 rounded",
                    selectedHere === 0
                      ? "text-text-muted bg-bg-base"
                      : selectedHere === allPerms.length
                        ? "text-accent bg-accent-subtle"
                        : "text-text-secondary bg-bg-raised",
                  ].join(" ")}
                >
                  {selectedHere} / {allPerms.length}
                </span>
              </button>

              {open && (
                <div className="px-4 pb-3">
                  {!disabled && (
                    <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                      <button type="button" onClick={() => applyDomain(d, "all")} className="text-2xs px-2 py-0.5 rounded border border-border text-text-secondary hover:text-text-primary hover:bg-bg-hover">
                        ✓ 全選
                      </button>
                      <button type="button" onClick={() => applyDomain(d, "none")} className="text-2xs px-2 py-0.5 rounded border border-border text-text-secondary hover:text-text-primary hover:bg-bg-hover">
                        ✗ 全清
                      </button>
                      <button type="button" onClick={() => applyDomain(d, "read")} className="text-2xs px-2 py-0.5 rounded border border-border text-text-secondary hover:text-text-primary hover:bg-bg-hover">
                        僅 read
                      </button>
                      <button type="button" onClick={() => applyDomain(d, "read_write")} className="text-2xs px-2 py-0.5 rounded border border-border text-text-secondary hover:text-text-primary hover:bg-bg-hover">
                        read + write
                      </button>
                    </div>
                  )}
                  <div className="space-y-1">
                    {perms.map((p) => {
                      const checked = value.has(p.id);
                      const dangerous = DANGEROUS_PERMISSIONS.has(p.id);
                      const blocked = !!actorPermissions && !actorPermissions.has(p.id) && !checked;
                      return (
                        <label
                          key={p.id}
                          className={[
                            "flex items-start gap-2.5 rounded px-2 py-1.5",
                            blocked || disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-bg-hover",
                          ].join(" ")}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={disabled || blocked}
                            onChange={() => togglePerm(p.id)}
                            className="mt-0.5 rounded border-border bg-bg-base text-accent focus:ring-accent focus:ring-offset-0"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-text-primary truncate">{p.label_zh}</span>
                              {dangerous && (
                                <span title="此為高風險權限（不可逆操作）" className="flex-shrink-0">
                                  <AlertTriangle className="h-3.5 w-3.5 text-danger" />
                                </span>
                              )}
                              <span className={[
                                "text-2xs px-1.5 py-0.5 rounded font-mono flex-shrink-0",
                                p.category === "read" ? "bg-bg-raised text-text-muted"
                                  : p.category === "write" ? "bg-warning/10 text-warning"
                                  : "bg-danger/10 text-danger",
                              ].join(" ")}>
                                {p.category}
                              </span>
                            </div>
                            <div className="text-2xs text-text-muted mt-0.5">
                              <code className="font-mono">{p.id}</code>
                              {p.description_zh && <span> · {p.description_zh}</span>}
                            </div>
                            {blocked && (
                              <div className="text-2xs text-danger mt-0.5">
                                您自己沒有這個權限，無法授予
                              </div>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer summary */}
      <div className="flex items-center justify-between text-xs text-text-secondary">
        <span>
          已選 <strong className="text-text-primary font-mono">{totalSelected}</strong> / {totalAvailable} 個權限
        </span>
        {actorPermissions && (
          <span className="text-text-muted">
            灰色項目代表您自己沒有的權限，無法授予
          </span>
        )}
      </div>
    </div>
  );
}
